import { ExclamationCircleIcon, XIcon } from '@heroicons/react/outline';
import { useLanguage } from '../contexts/LanguageContext';

export default function ErrorDisplay({ error, onDismiss, className = "" }) {
  const { t } = useLanguage();

  if (!error) return null;

  // Handle different error formats
  const getErrorContent = () => {
    // If error is a string, display it directly
    if (typeof error === 'string') {
      return {
        message: error,
        errors: []
      };
    }

    // If error has response data (axios error)
    if (error.response?.data) {
      const errorData = error.response.data;
      
      return {
        message: errorData.message || 'An error occurred',
        errors: errorData.errors || [],
        type: errorData.type || 'error'
      };
    }

    // If error is an object with message
    if (error.message) {
      return {
        message: error.message,
        errors: error.errors || [],
        type: error.type || 'error'
      };
    }

    // Fallback
    return {
      message: 'An unexpected error occurred',
      errors: []
    };
  };

  const { message, errors, type } = getErrorContent();

  // Get appropriate styling based on error type
  const getErrorStyles = () => {
    switch (type) {
      case 'unique_constraint':
        return {
          containerClass: 'bg-yellow-50 border-yellow-200',
          iconClass: 'text-yellow-400',
          titleClass: 'text-yellow-800',
          messageClass: 'text-yellow-700'
        };
      case 'validation':
        return {
          containerClass: 'bg-red-50 border-red-200',
          iconClass: 'text-red-400',
          titleClass: 'text-red-800',
          messageClass: 'text-red-700'
        };
      case 'forbidden':
        return {
          containerClass: 'bg-orange-50 border-orange-200',
          iconClass: 'text-orange-400',
          titleClass: 'text-orange-800',
          messageClass: 'text-orange-700'
        };
      case 'not_found':
        return {
          containerClass: 'bg-gray-50 border-gray-200',
          iconClass: 'text-gray-400',
          titleClass: 'text-gray-800',
          messageClass: 'text-gray-700'
        };
      default:
        return {
          containerClass: 'bg-red-50 border-red-200',
          iconClass: 'text-red-400',
          titleClass: 'text-red-800',
          messageClass: 'text-red-700'
        };
    }
  };

  const styles = getErrorStyles();

  return (
    <div className={`rounded-md border p-4 ${styles.containerClass} ${className}`}>
      <div className="flex">
        <div className="flex-shrink-0">
          <ExclamationCircleIcon className={`h-5 w-5 ${styles.iconClass}`} aria-hidden="true" />
        </div>
        <div className="ml-3 flex-1">
          <h3 className={`text-sm font-medium ${styles.titleClass}`}>
            {type === 'unique_constraint' && t('errors.duplicateEntry')}
            {type === 'validation' && t('errors.validationError')}
            {type === 'forbidden' && t('errors.accessDenied')}
            {type === 'not_found' && t('errors.notFound')}
            {!['unique_constraint', 'validation', 'forbidden', 'not_found'].includes(type) && t('errors.error')}
          </h3>
          <div className={`mt-2 text-sm ${styles.messageClass}`}>
            <p>{message}</p>
            {errors && errors.length > 0 && (
              <ul className="mt-2 list-disc list-inside space-y-1">
                {errors.map((fieldError, index) => (
                  <li key={index}>
                    <span className="font-medium">{getFieldDisplayName(fieldError.field)}:</span> {fieldError.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        {onDismiss && (
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                type="button"
                className={`inline-flex rounded-md p-1.5 ${styles.messageClass} hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600`}
                onClick={onDismiss}
              >
                <span className="sr-only">Dismiss</span>
                <XIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper function to get user-friendly field names
function getFieldDisplayName(fieldName) {
  const fieldMap = {
    clientNumber: 'Client Number',
    firstName: 'First Name',
    lastName: 'Last Name',
    fatherName: 'Father Name',
    grandfatherName: 'Grandfather Name',
    email: 'Email',
    mobile: 'Mobile Number',
    dateOfBirth: 'Date of Birth',
    address: 'Address',
    nationality: 'Nationality',
    passportNumber: 'Passport Number',
    licenseNumber: 'License Number',
    occupation: 'Occupation',
    nationalId: 'National ID',
    username: 'Username',
    password: 'Password',
    role: 'Role',
    companyId: 'Company',
    branchId: 'Branch',
    governingBodyId: 'Governing Body',
    name: 'Name',
    abbreviation: 'Abbreviation',
    phone: 'Phone',
    description: 'Description',
    parentGoverningBodyId: 'Parent Governing Body',
    vehicleMake: 'Vehicle Make',
    vehicleModel: 'Vehicle Model',
    vehicleYear: 'Vehicle Year',
    vehiclePlate: 'License Plate',
    chassisNumber: 'Chassis Number',
    designatedDriver: 'Designated Driver',
    designatedDriverLicense: 'Driver License Number',
    carColour: 'Car Color',
    enginePower: 'Engine Power',
    weight: 'Weight',
    vehicleHorsepower: 'Vehicle Horsepower',
    vehicleWeight: 'Vehicle Weight',
    passengerCount: 'Number of Passengers',
    authorizedPassengers: 'Authorized Passengers',
    mandatoryInsuranceType: 'Insurance Type',
    mandatoryInsuranceSubType: 'Insurance Sub-type',
    limitOfIndemnity: 'Limit of Indemnity',
    premium: 'Premium',
    totalPremium: 'Total Premium',
    startDate: 'Start Date',
    endDate: 'End Date',
    numberOfDays: 'Number of Days',
    paymentStatus: 'Payment Status',
    paymentMethod: 'Payment Method'
  };

  return fieldMap[fieldName] || fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
} 